import React, {
  useState,
  useEffect,
  KeyboardEvent,
  ChangeEvent,
  useRef,
  useContext,
  ReactNode,
} from "react";

import {
  PatientInfoForm,
  PatientInfoFormNoSubmit,
  PickUploadMethod,
  PoweredByClinicOS,
  RelatedQuestions,
  TitleBarAIHelper,
  Visualization,
} from "components/index";
import {
  ClinicContext,
  ScreenContext,
  SessionContext,
  ThemeContext,
} from "contexts";
import {
  postChatPlasticSurgery,
  postTransforms,
  TPostTransformsBatchResponse,
  TPostTransformsRequest,
  TPostTransformsResponse,
  TPostChatPlasticSurgeryRequest,
  TPostChatPlasticSurgeryResponse,
  TSubmitAdvertiseFormRequest,
  submitAdvertiseForm,
  TSubmitAdvertiseFormResponse,
  postLeadMessage,
  TPostLeadMessageRequest,
  TPostLeadMessageQuery,
} from "api/clinicos";
import {
  FormNameType,
  PostWindowEventName,
  ProcedureType,
  TFormName,
  TMessageSender,
  TScreen,
  UploadTypeType,
  ViewType,
} from "api/type";
import { useUserLocation } from "utils/customHook";

import Markdown from "react-markdown";
import { hexToRgba } from "utils/hexToRgba";
import { formatProcedureName } from "utils/formatProcedureName";
import {
  adjustTextMap,
  bblModels,
  breastAugModels,
  breastLiftModels,
  breastReductionModels,
  tummyTuckModels,
} from "api/constants";
import SelectAIProcedure from "components/SelectAIProcedure";
import JumpingDots from "components/jumping-dots/JumpingDots";
import Refresh from "assets/Refresh.icon";
import PatientInfoSelect from "components/PatientInfoSelect";
import { generateUUID } from "utils/generateUUID";
import { callFormSubmissionTracking } from "utils/callClinicosTracking";
import { mixpanelTrack } from "utils/mixpanel";
import {
  bodyProcedureTypes,
  faceProcedureTypes,
  hairProcedureTypes,
  vaginalProcedureTypes,
  breastProcedureTypes,
} from "api/constants";

type Message = {
  sender: "user" | "bot" | "system";
  content: string | ReactNode;
  typingContent?: string;
};

type ScreenChatBotProps = {
  batchId: string;
  afterScanning: boolean;
  isFormSubmitted: boolean;
  selectedProcedure: ProcedureType | null;
  postTransformsBatchResponse: TPostTransformsBatchResponse | null;
  handleSetIsFormSubmittedTrue: () => void;
  handleSaveSelectedProcedure: (procedure: ProcedureType) => void;
  firstMessageSent: boolean;
  handleSetFirstMessageSentToTrue: () => void;
};

const talkToAPatientCoordinator = "Talk to a Patient Coordinator";
const simulateAProcedureWithAI = "Simulate a Procedure";
const whichProcedureShouldWeStartWith = "Which procedure should we start with?";
const simulateOnAnotherProcedure = "Simulate on another procedure";
const whatIsYourLastName = "What is your last name?";
const whatIsYourPhoneNumber = "What is your phone number?";
const howOldAreYou = "How old are you?";
const whatIsYourHeight = "What is your height?";
const howMuchDoYouWeigh = "How much do you weigh?";
const idLikeToAskSomethingElse = "I'd like to ask something else";
const idLikeToSimulateMyNextProcedure =
  "I'd like to simulate my next procedure.";
const doAnyOfTheseApplyToYou = "Do any of these apply to you?";
const whatTypeOfProcedureAreYouInterestedIn =
  "What type of procedure are you interested in?";
const whichProcedureSpecifically = "Which procedure specifically?";
const whenWouldYouLikeToGetYourProcedure =
  "When would you like to get your procedure?";
const thankYou =
  "Thank you! Is there anything else you'd like to tell the patient coordinator?";
const thankYouACoordinatorWillReachOutToYouSoon =
  "Thank you! A coordinator will reach out to you soon.";
const greatPickOneFromBelow =
  "Great! Let's start with an example so you get an idea of what to expect.";
const letStartWithA = "Let's start with a procedure on an example image.";
const simulateAProcedureOnMyImages = "Simulate a procedure on my images";
const uploadAnotherImage = "Upload another image";
const greatCaptureAnImageWithOurSecureImageCaptureTool =
  "Great! Capture an image with our secure image capture tool.";
const enterYourInformationToSeeResult = "Enter your information to see result.";
const hereIsHowTheProcedureWouldLook = "Here’s how the procedure would look:";
const greatIllGrapAPatientCoordinator =
  "Great! I'll grab a patient coordinator. Let's answer a few questions about yourself.\n\n&ensp;\n\nWhat is your first name?";
const greatTypeYourMessageBelow = "Great! Type your message below:";
const welcome =
  "Welcome! I'm Paige, your AI assistant at Top Aesthetics.\n\n&ensp;\n\nI can use AI to help you visualize your next procedure or connect you with a Patient Coordinator for personalized guidance.\n\n&ensp;\n\nHow can I assist you today?";

const ScreenChatBot: React.FC<ScreenChatBotProps> = ({
  batchId,
  afterScanning,
  isFormSubmitted,
  selectedProcedure,
  postTransformsBatchResponse,
  handleSetIsFormSubmittedTrue,
  handleSaveSelectedProcedure,
  firstMessageSent,
  handleSetFirstMessageSentToTrue,
}) => {
  const theme = React.useContext(ThemeContext);
  const session = useContext(SessionContext);
  const clinic = useContext(ClinicContext);
  const setScreen = useContext(ScreenContext)[1];

  const fetchUserLocation = useUserLocation(session);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [userInput, setUserInput] = useState<string>("");
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [followUpQuestions, setFollowUpQuestions] = useState<string[]>([]);
  const [history, setHistory] = useState<
    { prompt: string; response: string }[]
  >([]);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  useState<boolean>(false);
  const [showSelectProcedure, setShowSelectProcedure] =
    useState<boolean>(false);
  const selectedProcedureRef = useRef<ProcedureType | null>(selectedProcedure);

  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [phone, setPhone] = useState<string>("");

  const [showCaptureImage, setShowCaptureImage] = useState<boolean>(false);
  const [collectingUserInfoMode, setCollectingUserInfoMode] = useState<
    | "FirstName"
    | "LastName"
    | "Phone"
    | "Age"
    | "Height"
    | "Weight"
    | "Conditions"
    | "Type Of Procedure"
    | "Specific Procedure"
    | "Procedure Time"
    | "Message"
    | false
  >(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    window.addEventListener("message", (ev) => {
      const payLoad = ev.data;
      if (payLoad.message === PostWindowEventName.FLAG_AUTO_FOCUS) {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 1000);
      }
    });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    let lastTextMessageIndex = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (
        typeof (messages[i].content === "string") &&
        messages[i].sender === "bot"
      ) {
        lastTextMessageIndex = i;
        break;
      }
    }
    if (!lastTextMessageIndex) return;

    let i = messages[lastTextMessageIndex]?.typingContent?.length || 0;
    const typingInterval = setInterval(() => {
      // @ts-ignore
      if (i < messages[lastTextMessageIndex]?.content?.length) {
        setMessages((prevMessages) => {
          const updatedMessages = [...prevMessages];
          const currentMessage = updatedMessages[lastTextMessageIndex];
          // @ts-ignore
          currentMessage.typingContent = currentMessage?.content?.substring(
            0,
            i + 1,
          );
          return updatedMessages;
        });
        i++;
      } else {
        clearInterval(typingInterval);
      }
    }, 10);

    return () => clearInterval(typingInterval);
  }, [messages]);

  useEffect(() => {
    if (messages.length !== 0) return;
    if (!afterScanning) {
      setMessages([
        {
          sender: "bot",
          content: welcome,
        },
      ]);
      setFollowUpQuestions([
        simulateAProcedureWithAI,
        talkToAPatientCoordinator,
      ]);
      return;
    }
    if (!postTransformsBatchResponse) {
      setIsTyping(true);
      setMessages([]);
      return;
    }

    setIsTyping(false);
    if (isFormSubmitted) {
      setFollowUpQuestions([simulateOnAnotherProcedure, uploadAnotherImage]);

      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: "bot",
          content: hereIsHowTheProcedureWouldLook.replace(
            "procedure",
            selectedProcedureRef.current ?? "procedure",
          ),
        },
        {
          sender: "system",
          content: (
            <div style={{ width: 200, margin: "0 auto" }}>
              {postTransformsBatchResponse && (
                <Visualization
                  afterImageUrls={
                    postTransformsBatchResponse.response[0].presigned_urls
                  }
                  procedureDisplayMessage={
                    adjustTextMap[selectedProcedureRef.current || ""]
                  }
                  metaData={postTransformsBatchResponse.response[0].metadata}
                />
              )}
            </div>
          ),
        },
      ]);
      return;
    }

    setMessages((prevMessages) => [
      ...prevMessages,
      {
        sender: "bot",
        content: enterYourInformationToSeeResult,
      },
      {
        sender: "system",
        content: (
          <PatientInfoForm
            batchId={batchId}
            uploadType={UploadTypeType.SCAN}
            selectedProcedure={selectedProcedureRef.current}
            handleSubmit={() => {
              handleSetIsFormSubmittedTrue();
              setFollowUpQuestions([
                simulateOnAnotherProcedure,
                uploadAnotherImage,
              ]);
              setMessages((prevMessages) => [
                ...prevMessages,
                {
                  sender: "bot",
                  content: `Here’s how the ${formatProcedureName(
                    selectedProcedureRef.current,
                  )} would look:`,
                },
                {
                  sender: "system",
                  content: (
                    <div style={{ width: 200, margin: "0 auto" }}>
                      {postTransformsBatchResponse && (
                        <Visualization
                          afterImageUrls={
                            postTransformsBatchResponse.response[0]
                              .presigned_urls
                          }
                          procedureDisplayMessage={
                            adjustTextMap[selectedProcedureRef.current || ""]
                          }
                          metaData={
                            postTransformsBatchResponse.response[0].metadata
                          }
                        />
                      )}
                    </div>
                  ),
                },
              ]);
            }}
          />
        ),
      },
    ]);
    // eslint-disable-next-line
  }, [afterScanning, postTransformsBatchResponse]);

  const fakeTyping = async () => {
    setIsTyping(true);
    await new Promise((resolve) => setTimeout(resolve, 500));
    setIsTyping(false);
  };

  const handleGetPatientCoordinator = async (message: string) => {
    if (message === talkToAPatientCoordinator) {
      mixpanelTrack("Patient Coordinator Button Clicked", [
        {
          propertyName: "Clinic Name",
          propertyValue: clinic?.clinic_name ?? "failed to get clinic name",
        },
      ]);
      if (isFormSubmitted) {
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            sender: "bot",
            content: greatTypeYourMessageBelow,
          },
          {
            sender: "system",
            content: (
              <PatientInfoFormNoSubmit
                collectMessage
                handleSubmit={() => {
                  // TODO: Call API to send message to patient coordinator
                }}
              />
            ),
          },
        ]);
        setFollowUpQuestions([idLikeToAskSomethingElse]);
        return true;
      } else {
        setCollectingUserInfoMode("LastName");
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            sender: "user",
            content: talkToAPatientCoordinator,
          },
        ]);
        await fakeTyping();
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            sender: "bot",
            content: greatIllGrapAPatientCoordinator,
          },
        ]);
        setFollowUpQuestions([idLikeToAskSomethingElse]);
        return true;
      }
    }

    if (collectingUserInfoMode === "LastName") {
      setCollectingUserInfoMode("Phone");
      setFirstName(message);
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: "user",
          content: message,
        },
      ]);
      await fakeTyping();
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: "bot",
          content: whatIsYourLastName,
        },
      ]);
      return true;
    }

    if (collectingUserInfoMode === "Phone") {
      setCollectingUserInfoMode("Age");
      setLastName(message);
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: "user",
          content: message,
        },
      ]);
      await fakeTyping();
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: "bot",
          content: whatIsYourPhoneNumber,
        },
      ]);
      return true;
    }

    if (collectingUserInfoMode === "Age") {
      setCollectingUserInfoMode("Height");
      setPhone(message);
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: "user",
          content: message,
        },
      ]);
      await fakeTyping();
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: "bot",
          content: howOldAreYou,
        },
      ]);
      return true;
    }

    if (collectingUserInfoMode === "Height") {
      setCollectingUserInfoMode("Weight");
      const age = message;
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: "user",
          content: message,
        },
      ]);
      await fakeTyping();
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: "bot",
          content: whatIsYourHeight,
        },
        {
          sender: "system",
          content: (
            <PatientInfoFormNoSubmit
              collectHeight
              handleSubmit={async (value) => {
                await fakeTyping();
                const height = value;
                setMessages((prevMessages) => [
                  ...prevMessages,
                  {
                    sender: "bot",
                    content: howMuchDoYouWeigh,
                  },
                  {
                    sender: "system",
                    content: (
                      <PatientInfoFormNoSubmit
                        collectWeigh
                        handleSubmit={async (value) => {
                          const weigh = value;
                          await fakeTyping();
                          setMessages((prevMessages) => [
                            ...prevMessages,
                            {
                              sender: "bot",
                              content: doAnyOfTheseApplyToYou,
                            },
                            {
                              sender: "system",
                              content: (
                                <PatientInfoSelect
                                  selections={[
                                    "I have diabetes",
                                    "I have a history of blood clots",
                                    "I have cancer",
                                    "I have had an organ transplant",
                                    "I have heart disease",
                                    "None of the above",
                                  ]}
                                  onSubmit={async (values) => {
                                    const healthHistory = values;
                                    await fakeTyping();
                                    setMessages((prevMessages) => [
                                      ...prevMessages,
                                      {
                                        sender: "bot",
                                        content:
                                          whatTypeOfProcedureAreYouInterestedIn,
                                      },
                                      {
                                        sender: "system",
                                        content: (
                                          <PatientInfoSelect
                                            selections={[
                                              "Body",
                                              "Breast",
                                              "Face",
                                              "Hair",
                                              "Vaginal",
                                            ]}
                                            onSubmit={(values) => {
                                              const procedureAreas = values;
                                              const procedureSelections: ProcedureType[] =
                                                [];
                                              if (
                                                procedureAreas.includes("Body")
                                              ) {
                                                procedureSelections.push(
                                                  ...bodyProcedureTypes,
                                                );
                                              }
                                              if (
                                                procedureAreas.includes(
                                                  "Breast",
                                                )
                                              ) {
                                                procedureSelections.push(
                                                  ...breastProcedureTypes,
                                                );
                                              }
                                              if (
                                                procedureAreas.includes("Face")
                                              ) {
                                                procedureSelections.push(
                                                  ...faceProcedureTypes,
                                                );
                                              }
                                              if (
                                                procedureAreas.includes("Hair")
                                              ) {
                                                procedureSelections.push(
                                                  ...hairProcedureTypes,
                                                );
                                              }
                                              if (
                                                procedureAreas.includes(
                                                  "Vaginal",
                                                )
                                              ) {
                                                procedureSelections.push(
                                                  ...vaginalProcedureTypes,
                                                );
                                              }
                                              setMessages((prevMessages) => [
                                                ...prevMessages,
                                                {
                                                  sender: "bot",
                                                  content:
                                                    whichProcedureSpecifically,
                                                },
                                                {
                                                  sender: "system",
                                                  content: (
                                                    <PatientInfoSelect
                                                      selections={
                                                        procedureSelections
                                                      }
                                                      multiple
                                                      onSubmit={async (
                                                        values,
                                                      ) => {
                                                        await fakeTyping();
                                                        const procedures =
                                                          values;
                                                        setMessages(
                                                          (prevMessages) => [
                                                            ...prevMessages,
                                                            {
                                                              sender: "bot",
                                                              content:
                                                                whenWouldYouLikeToGetYourProcedure,
                                                            },
                                                            {
                                                              sender: "system",
                                                              content: (
                                                                <PatientInfoSelect
                                                                  multiple={
                                                                    false
                                                                  }
                                                                  selections={[
                                                                    "ASAP",
                                                                    "Within the next 3 months",
                                                                    "In 3-6 months",
                                                                    "In 6-12 months",
                                                                    "Not sure yet",
                                                                  ]}
                                                                  onSubmit={async (
                                                                    values,
                                                                  ) => {
                                                                    await fakeTyping();
                                                                    let submitResponse: TSubmitAdvertiseFormResponse;
                                                                    let procedureTime =
                                                                      "";
                                                                    if (
                                                                      values.length >
                                                                      0
                                                                    )
                                                                      procedureTime =
                                                                        values[0];

                                                                    try {
                                                                      if (
                                                                        !session
                                                                      )
                                                                        return;
                                                                      const body: TSubmitAdvertiseFormRequest =
                                                                        {
                                                                          ...(batchId
                                                                            ? {
                                                                                batch_id:
                                                                                  batchId,
                                                                              }
                                                                            : {}),
                                                                          form_user_id:
                                                                            generateUUID(),
                                                                          form_name:
                                                                            FormNameType.ONBOARDING_TIER1_CHAT,
                                                                          is_debug:
                                                                            process
                                                                              .env
                                                                              .REACT_APP_ENV ===
                                                                            "production"
                                                                              ? false
                                                                              : true,
                                                                          form_data:
                                                                            {
                                                                              firstName:
                                                                                firstName,
                                                                              lastName:
                                                                                lastName,
                                                                              phone:
                                                                                phone,
                                                                              age: age,
                                                                              weigh:
                                                                                weigh,
                                                                              height:
                                                                                height,
                                                                              healthHistory:
                                                                                healthHistory,
                                                                              procedureAreas:
                                                                                procedureAreas,
                                                                              procedures:
                                                                                procedures,
                                                                              procedureTime:
                                                                                procedureTime,
                                                                            },
                                                                          patient_record:
                                                                            {
                                                                              first_name:
                                                                                firstName,
                                                                              last_name:
                                                                                lastName,
                                                                              phone:
                                                                                phone,
                                                                              location:
                                                                                await fetchUserLocation(),
                                                                              procedures_of_interest:
                                                                                procedures as ProcedureType[],
                                                                              upload_type:
                                                                                UploadTypeType.CHATBOT,
                                                                            },
                                                                        };
                                                                      submitResponse =
                                                                        await submitAdvertiseForm(
                                                                          session,
                                                                          body,
                                                                        );
                                                                      console.log(
                                                                        "submitResponse",
                                                                        submitResponse,
                                                                      );
                                                                      mixpanelTrack(
                                                                        "User Details Captured",
                                                                        [
                                                                          {
                                                                            propertyName:
                                                                              "Clinic Name",
                                                                            propertyValue:
                                                                              clinic?.clinic_name ??
                                                                              "failed to get clinic name",
                                                                          },
                                                                        ],
                                                                      );
                                                                      mixpanelTrack(
                                                                        "Form Submitted",
                                                                        [
                                                                          {
                                                                            propertyName:
                                                                              "Clinic Name",
                                                                            propertyValue:
                                                                              clinic?.clinic_name ??
                                                                              "failed to get clinic name",
                                                                          },
                                                                        ],
                                                                      );
                                                                      callFormSubmissionTracking(
                                                                        session,
                                                                        TFormName.ONBOARDING_TIER1,
                                                                      );
                                                                    } catch (error) {
                                                                    } finally {
                                                                    }

                                                                    handleSetIsFormSubmittedTrue();
                                                                    setMessages(
                                                                      (
                                                                        prevMessages,
                                                                      ) => [
                                                                        ...prevMessages,
                                                                        {
                                                                          sender:
                                                                            "bot",
                                                                          content:
                                                                            thankYou,
                                                                        },
                                                                        {
                                                                          sender:
                                                                            "system",
                                                                          content:
                                                                            (
                                                                              <RelatedQuestions
                                                                                questions={[
                                                                                  "Yes, I'd like to ask something",
                                                                                  "No",
                                                                                ]}
                                                                                onClick={async (
                                                                                  value,
                                                                                ) => {
                                                                                  await fakeTyping();
                                                                                  if (
                                                                                    value ===
                                                                                    "No"
                                                                                  ) {
                                                                                    setFollowUpQuestions(
                                                                                      [],
                                                                                    );
                                                                                    setMessages(
                                                                                      (
                                                                                        prevMessages,
                                                                                      ) => [
                                                                                        ...prevMessages,
                                                                                        {
                                                                                          sender:
                                                                                            "bot",
                                                                                          content:
                                                                                            thankYouACoordinatorWillReachOutToYouSoon,
                                                                                        },
                                                                                      ],
                                                                                    );
                                                                                  } else {
                                                                                    setMessages(
                                                                                      (
                                                                                        prevMessages,
                                                                                      ) => [
                                                                                        ...prevMessages,
                                                                                        {
                                                                                          sender:
                                                                                            "bot",
                                                                                          content:
                                                                                            greatTypeYourMessageBelow,
                                                                                        },
                                                                                        {
                                                                                          sender:
                                                                                            "system",
                                                                                          content:
                                                                                            (
                                                                                              <PatientInfoFormNoSubmit
                                                                                                collectMessage
                                                                                                handleSubmit={async (
                                                                                                  message,
                                                                                                ) => {
                                                                                                  setFollowUpQuestions(
                                                                                                    [],
                                                                                                  );
                                                                                                  if (
                                                                                                    !submitResponse ||
                                                                                                    !session
                                                                                                  )
                                                                                                    return;
                                                                                                  try {
                                                                                                    const body: TPostLeadMessageRequest =
                                                                                                      {
                                                                                                        message:
                                                                                                          message,
                                                                                                        sender_type:
                                                                                                          TMessageSender.PATIENT,
                                                                                                      };
                                                                                                    const query: TPostLeadMessageQuery =
                                                                                                      {
                                                                                                        patient_uuid:
                                                                                                          submitResponse.patient_uuid,
                                                                                                      };
                                                                                                    await postLeadMessage(
                                                                                                      session,
                                                                                                      query,
                                                                                                      body,
                                                                                                    );
                                                                                                  } catch (error) {
                                                                                                  } finally {
                                                                                                    await fakeTyping();
                                                                                                    setMessages(
                                                                                                      (
                                                                                                        prevMessages,
                                                                                                      ) => [
                                                                                                        ...prevMessages,
                                                                                                        {
                                                                                                          sender:
                                                                                                            "bot",
                                                                                                          content:
                                                                                                            thankYouACoordinatorWillReachOutToYouSoon,
                                                                                                        },
                                                                                                      ],
                                                                                                    );
                                                                                                  }
                                                                                                }}
                                                                                              />
                                                                                            ),
                                                                                        },
                                                                                      ],
                                                                                    );
                                                                                  }
                                                                                }}
                                                                              />
                                                                            ),
                                                                        },
                                                                      ],
                                                                    );
                                                                  }}
                                                                />
                                                              ),
                                                            },
                                                          ],
                                                        );
                                                      }}
                                                    />
                                                  ),
                                                },
                                              ]);
                                            }}
                                          />
                                        ),
                                      },
                                    ]);
                                  }}
                                />
                              ),
                            },
                          ]);
                        }}
                      />
                    ),
                  },
                ]);
              }}
            />
          ),
        },
      ]);

      return true;
    }

    return false;
  };

  const handleOnClickMessage = async (
    message: string,
    updateUserMessage: boolean = true,
  ) => {
    if (!firstMessageSent) {
      mixpanelTrack("Form Started", [
        {
          propertyName: "Clinic Name",
          propertyValue: clinic?.clinic_name ?? "failed to get clinic name",
        },
      ]);
      mixpanelTrack("First Message Sent", [
        {
          propertyName: "Clinic Name",
          propertyValue: clinic?.clinic_name ?? "failed to get clinic name",
        },
      ]);
      handleSetFirstMessageSentToTrue();
    }
    setUserInput("");

    if (message === idLikeToAskSomethingElse) {
      setCollectingUserInfoMode(false);
    }

    if (
      [simulateAProcedureWithAI, simulateOnAnotherProcedure].includes(message)
    ) {
      setFollowUpQuestions([]);
      if (updateUserMessage)
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            sender: "user",
            content: idLikeToSimulateMyNextProcedure,
          },
        ]);

      await fakeTyping();

      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: "bot",
          content: whichProcedureShouldWeStartWith,
        },
      ]);
      setShowSelectProcedure(true);

      return;
    }

    if ([simulateAProcedureOnMyImages, uploadAnotherImage].includes(message)) {
      setFollowUpQuestions([]);
      await fakeTyping();
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: "bot",
          content: greatCaptureAnImageWithOurSecureImageCaptureTool,
        },
      ]);
      setShowCaptureImage(true);
      return;
    }

    if (
      message !== idLikeToAskSomethingElse &&
      (await handleGetPatientCoordinator(message))
    )
      return;

    if (!session) {
      console.error("Session is null");
      return;
    }

    setMessages((prevMessages) => [
      ...prevMessages,
      {
        sender: "user",
        content: message,
      },
    ]);
    setUserInput("");
    setIsTyping(true);

    try {
      const body: TPostChatPlasticSurgeryRequest = {
        content: message,
        temperature: 0.5,
        max_tokens: 500,
        history: history.slice(-2),
      };

      const response: TPostChatPlasticSurgeryResponse =
        await postChatPlasticSurgery(session, body);

      if (response.should_collect_contact_info) {
        if (isFormSubmitted) {
          setMessages((prevMessages) => [
            ...prevMessages,
            {
              sender: "bot",
              content: greatTypeYourMessageBelow,
            },
            {
              sender: "system",
              content: (
                <PatientInfoFormNoSubmit
                  collectMessage
                  handleSubmit={() => {
                    setFollowUpQuestions([]);
                    handleSetIsFormSubmittedTrue();
                  }}
                />
              ),
            },
          ]);
        }
      } else if (response.should_simulate && !response.procedure_to_simulate) {
        setShowSelectProcedure(true);
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            sender: "bot",
            content: whichProcedureShouldWeStartWith,
          },
        ]);
      } else if (response.should_simulate && response.procedure_to_simulate) {
        selectedProcedureRef.current = response.procedure_to_simulate;
        const content = await renderSelectModel(response.procedure_to_simulate);
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            sender: "bot",
            content: `Here’s how the ${formatProcedureName(
              selectedProcedureRef.current,
            )} would look:`,
          },
          {
            sender: "system",
            content: content,
          },
        ]);
      } else {
        setMessages((prevMessages) => [
          ...prevMessages,
          { sender: "bot", content: response.response, displayedContent: "" },
        ]);
      }

      setHistory((prevHistory) => [
        ...prevHistory,
        { prompt: message, response: response.response },
      ]);
      if (response.should_collect_contact_info) {
        setFollowUpQuestions((prev) => [...prev, idLikeToAskSomethingElse]);
      }

      if (message === idLikeToAskSomethingElse) {
        setFollowUpQuestions([
          simulateAProcedureWithAI,
          simulateOnAnotherProcedure,
        ]);
      }
    } catch (error: any) {
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: "bot",
          content: "Sorry, something went wrong.",
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleSelectProcedure = async (procedure: ProcedureType) => {
    handleSaveSelectedProcedure(procedure);
    setFollowUpQuestions([]);
    setShowSelectProcedure(false);
    selectedProcedureRef.current = procedure;

    setMessages((prevMessages) => [
      ...prevMessages,
      {
        sender: "user",
        content: letStartWithA.replace(
          "procedure",
          formatProcedureName(procedure),
        ),
      },
    ]);

    await fakeTyping();
    const content = await renderSelectModel(procedure);
    setMessages((prevMessages) => [
      ...prevMessages,
      {
        sender: "bot",
        content: `Here’s how the ${formatProcedureName(
          selectedProcedureRef.current,
        )} would look:`,
      },
      {
        sender: "system",
        content: content,
      },
    ]);
    mixpanelTrack("Procedure Selected", [
      {
        propertyName: "Clinic Name",
        propertyValue: clinic?.clinic_name ?? "failed to get clinic name",
      },
      { propertyName: "Procedure", propertyValue: procedure },
    ]);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setUserInput(event.target.value);
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    inputRef.current?.focus();
    if (isTyping) return;
    if (event.key === "Enter" && userInput.trim() !== "") {
      handleOnClickMessage(userInput);
    }
  };

  const renderSelectModel = async (procedure: ProcedureType | null) => {
    try {
      let models: string[];
      switch (procedure) {
        case ProcedureType.BREAST_AUGMENTATION:
          models = breastAugModels;
          break;
        case ProcedureType.BREAST_LIFT:
          models = breastLiftModels;
          break;
        case ProcedureType.BREAST_REDUCTION:
          models = breastReductionModels;
          break;
        case ProcedureType.TUMMY_TUCK:
          models = tummyTuckModels;
          break;
        case ProcedureType.BBL:
          models = bblModels;
          break;
        case null:
          return <p>Please pass in a procedure in the url.</p>;
        default:
          return (
            <p>
              Your procedure is not supported yet or you passed in an invalid
              input.
            </p>
          );
      }

      if (!session) throw new Error("Session is null");
      if (!selectedProcedureRef.current) throw new Error("Procedure is null");
      if (models.length === 0) throw new Error("Models array is empty");

      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: "bot",
          content: greatPickOneFromBelow,
        },
        {
          sender: "system",
          content: (
            <div
              style={{ display: "flex", flex: 1, justifyContent: "flex-end" }}
            >
              <img
                style={{
                  height: 150,
                  marginLeft: "auto",
                }}
                src={models[0]}
                alt="Updated content"
              />
            </div>
          ),
        },
      ]);

      setFollowUpQuestions([
        simulateAProcedureOnMyImages,
        talkToAPatientCoordinator,
      ]);
      setTimeout(() => scrollToBottom(), 100);
      setIsTyping(true);

      let viewType: ViewType;
      switch (selectedProcedureRef.current) {
        case ProcedureType.BREAST_AUGMENTATION:
        case ProcedureType.BREAST_REDUCTION:
        case ProcedureType.BREAST_LIFT:
        case ProcedureType.BREAST_LIFT_AND_AUGMENTATION:
          viewType = ViewType.TORSO_FRONT;
          break;
        case ProcedureType.TUMMY_TUCK:
          viewType = ViewType.BODY_FRONT;
          break;
        case ProcedureType.BBL:
          viewType = ViewType.BODY_BACK;
          break;
        default:
          viewType = ViewType.TORSO_FRONT;
      }

      const body: TPostTransformsRequest = {
        image_url: models[0],
        procedure_types: [selectedProcedureRef.current],
        procedure_view: viewType,
      };
      const result: TPostTransformsResponse = await postTransforms(
        session,
        body,
      );

      setFollowUpQuestions([
        simulateAProcedureOnMyImages,
        talkToAPatientCoordinator,
      ]);
      setTimeout(() => scrollToBottom(), 100);

      return (
        <div style={{ width: 200, margin: "0 auto" }}>
          <Visualization
            afterImageUrls={result.download_urls}
            procedureDisplayMessage={
              adjustTextMap[selectedProcedureRef.current || ""]
            }
            metaData={result.metadata}
          />
        </div>
      );
    } catch (error) {
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: "bot",
          content: "Sorry, something went wrong.",
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <div className="screen-container" style={{ gap: 0 }}>
      <TitleBarAIHelper
        onClickBackButton={() => window.location.reload()}
        backButton={<Refresh />}
      />
      <div className="screen-container" style={{ padding: 15 }}>
        <div
          className="remove_scroll_bar"
          style={{
            flex: 1,
            overflowY: "auto",
          }}
        >
          {messages.map((msg: Message, index) => {
            if (msg.sender === "system") {
              return <div style={{ marginBottom: 10 }}>{msg.content}</div>;
            }
            return (
              <div
                key={index}
                style={{
                  display: "flex",
                  maxWidth: "85%",
                  padding: "10px",
                  borderRadius: "10px",
                  width: "fit-content",
                  flexDirection: "column",
                  background:
                    msg.sender === "user"
                      ? hexToRgba(theme?.themeColor || "#00B7F4", 0.1)
                      : "#F3F3F3",
                  marginLeft: msg.sender === "user" ? "auto" : "0",
                  marginBottom: "10px",
                }}
              >
                {/* @ts-ignore */}
                <Markdown>{msg?.typingContent || msg.content}</Markdown>
              </div>
            );
          })}
          {isTyping && (
            <div
              style={{
                padding: "10px",
                textAlign: "left",
                borderRadius: "5px",
                background: "#eeeeee",
                display: "inline-block",
              }}
            >
              <JumpingDots />
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        {showSelectProcedure && (
          <SelectAIProcedure handleSelectProcedure={handleSelectProcedure} />
        )}
        {showCaptureImage && (
          <PickUploadMethod
            onClickScan={() => {
              setScreen(TScreen.BODY_SCAN_INSTRUCTIONS_SCREEN);
            }}
          />
        )}
        <RelatedQuestions
          questions={followUpQuestions}
          onClick={handleOnClickMessage}
        />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            position: "relative",
            borderRadius: "5px",
            background: hexToRgba(theme?.themeColor || "#00B7F4", 0.1),
            border: "1px solid #000",
            height: 45,
          }}
        >
          <input
            style={{
              border: "none",
              background: "transparent",
              position: "absolute",
              inset: "0px 50px 0px 20px",
              outline: "none",
              fontSize: "16px",
              fontWeight: 600,
              color: "black",
            }}
            ref={inputRef}
            value={userInput}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder="Type your message here"
            autoFocus
          />
          <div
            style={{
              zIndex: 1,
              position: "absolute",
              right: 20,
              cursor: userInput.trim() === "" ? "not-allowed" : "pointer",
            }}
            onClick={() => {
              if (isTyping) return;
              handleOnClickMessage(userInput);
              inputRef.current?.focus();
            }}
          >
            <svg
              width="19"
              height="20" // Increased height to 20
              viewBox="0 0 19 20" // Adjusted viewBox height as well
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0 16V0L19 8L0 16ZM2 13L13.85 8L2 3V6.5L8 8L2 9.5V13ZM2 13V3V9.5V13Z"
                fill={userInput.trim() !== "" ? "#00B7F4" : "#000"}
                transform="translate(0,4)" // Keeps the icon moved down
              />
            </svg>
          </div>
        </div>

        <PoweredByClinicOS />
      </div>
    </div>
  );
};

export default ScreenChatBot;
